import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { GoogleLogin } from "@react-oauth/google";
import { useToast } from "../components/ToastContext";
import "./Auth.css";
import { useTranslation } from "react-i18next";
import "../i18n";

const Login = () => {
  const { t } = useTranslation();
  const { login, googleLogin } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const from     = location.state?.from?.pathname || "/admin/dashboard";

  const [form, setForm]       = useState({ email: "", password: "" });
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw]   = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setTimeout(() => setMounted(true), 50); }, []);

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      await login(form.email, form.password);
      showToast("Welcome back! Login successful.", "success");
      navigate(from, { replace: true });
    } catch (err) {
      if (err.message.includes("429") || err.message.includes("Too Many")) {
        const msg = "Too many failed attempts. Please wait a minute before trying again.";
        setError(msg);
        showToast(msg, "warning");
      } else {
        const msg = err.message || "Invalid credentials. Please try again.";
        setError(msg);
        showToast(msg, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError(""); setLoading(true);
    try {
      await googleLogin(credentialResponse.credential);
      showToast("Google sign-in successful!", "success");
      navigate(from, { replace: true });
    } catch (err) {
      const msg = err.message || "Google sign-in failed. Please try again.";
      setError(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    const msg = "Google sign-in was cancelled or failed. Please try again.";
    setError(msg);
    showToast(msg, "error");
  };

  return (
    <div className={`auth-page ${mounted ? "auth-page--in" : ""}`}>
      <div className="auth-bg">
        <div className="auth-orb auth-orb--1"></div><div className="auth-orb auth-orb--2"></div>
        <div className="auth-orb auth-orb--3"></div><div className="auth-orb auth-orb--4"></div>
        <div className="auth-noise"></div><div className="auth-grid"></div>
      </div>
      <div className="auth-container">
        <div className="auth-hero">
          <div className="auth-hero-inner">
            <Link to="/" style={{ textDecoration: "none" }}>
              <div className="auth-hero-logo">
              <img src={`${process.env.PUBLIC_URL}/SolviotechFavicon.ico`} alt="Solviotech" style={{ height: "40px", width: "40px", objectFit: "contain", borderRadius: "8px" }} />
                <div className="auth-hero-logo-text">Solvio<strong>Tech</strong></div>
              </div>
            </Link>
            <Link to="/" className="auth-back-link"><i className="fa-solid fa-arrow-left"></i> {t("auth.backToSite")}</Link>
            <div className="auth-hero-tag"><span className="auth-hero-tag-dot"></span>Admin Console · Nairobi, Kenya</div>
            <h1 className="auth-hero-heading">Command your<br /><em className="auth-hero-em">digital platform</em></h1>
            <p className="auth-hero-desc">Everything you need to manage Solviotech — projects, team, services, and content — from one secure dashboard.</p>
            <div className="auth-hero-stats">
              {[{ val: "50+", label: "Projects" },{ val: "30+", label: "Clients" },{ val: "5", label: "Countries" },{ val: "98%", label: "Satisfaction" }].map((s) => (
                <div key={s.label} className="auth-stat"><span className="auth-stat-val">{s.val}</span><span className="auth-stat-label">{s.label}</span></div>
              ))}
            </div>
            <div className="auth-mockup">
              <div className="auth-mockup-header">
                <div className="auth-mockup-dots"><span></span><span></span><span></span></div>
                <div className="auth-mockup-title">admin.solviotech.com</div>
              </div>
              <div className="auth-mockup-body">
                <div className="auth-mockup-sidebar">
                  {["Dashboard","Projects","Team","Messages"].map((item, i) => (
                    <div key={item} className={`auth-mockup-nav ${i === 0 ? "auth-mockup-nav--active" : ""}`}>
                      <div className="auth-mockup-nav-icon"></div><span>{item}</span>
                    </div>
                  ))}
                </div>
                <div className="auth-mockup-content">
                  <div className="auth-mockup-cards">
                    {[{ c: "#0a9b7c", v: "50+" },{ c: "#c3b996", v: "30+" },{ c: "#60a5fa", v: "3" }].map((card, i) => (
                      <div key={i} className="auth-mockup-card">
                        <div className="auth-mockup-card-dot" style={{ background: card.c }}></div>
                        <div className="auth-mockup-card-val" style={{ color: card.c }}>{card.v}</div>
                      </div>
                    ))}
                  </div>
                  <div className="auth-mockup-bars">
                    {[40,65,45,80,55,90,70,60].map((h, i) => <div key={i} className="auth-mockup-bar" style={{ height: `${h}%` }}></div>)}
                  </div>
                </div>
              </div>
            </div>
            <div className="auth-trust">
              {[{ icon: "fa-solid fa-shield-halved", text: "JWT Secured" },{ icon: "fa-solid fa-lock", text: "256-bit Encrypted" },{ icon: "fa-solid fa-bolt", text: "Real-time Sync" }].map((t2) => (
                <div key={t2.text} className="auth-trust-item"><i className={t2.icon}></i>{t2.text}</div>
              ))}
            </div>
          </div>
        </div>

        <div className="auth-panel">
          <div className="auth-card">
            <div className="auth-card-top">
            <img src={`${process.env.PUBLIC_URL}/SolviotechFavicon.ico`} alt="Solviotech" style={{ height: "36px", width: "36px", objectFit: "contain", borderRadius: "8px" }} />
              <div>
                <h2 className="auth-card-title">{t("auth.welcomeBack")}</h2>
                <p className="auth-card-sub">{t("auth.signInSub")}</p>
              </div>
            </div>
            {error && <div className="auth-alert"><i className="fa-solid fa-circle-exclamation"></i> {error}</div>}
            <form onSubmit={handleSubmit} noValidate className="auth-form-body">
              <div className="auth-field">
                <label className="auth-label">{t("auth.emailLabel")}</label>
                <div className="auth-input-wrap">
                  <i className="fa-solid fa-at auth-input-icon"></i>
                  <input type="email" name="email" className="auth-input" placeholder={t("auth.emailPlaceholder")} value={form.email} onChange={handleChange} required autoComplete="email" />
                </div>
              </div>
              <div className="auth-field">
                <div className="auth-label-row">
                  <label className="auth-label">{t("auth.passwordLabel")}</label>
                  <button type="button" className="auth-link-btn">{t("auth.forgotAccess")}</button>
                </div>
                <div className="auth-input-wrap">
                  <i className="fa-solid fa-key auth-input-icon"></i>
                  <input type={showPw ? "text" : "password"} name="password" className="auth-input" placeholder={t("auth.passwordPlaceholder")} value={form.password} onChange={handleChange} required autoComplete="current-password" />
                  <button type="button" className="auth-eye-btn" onClick={() => setShowPw(v => !v)}>
                    <i className={`fa-solid ${showPw ? "fa-eye-slash" : "fa-eye"}`}></i>
                  </button>
                </div>
              </div>
              <label className="auth-check">
                <input type="checkbox" className="auth-check-input" />
                <span className="auth-check-box"></span>
                <span className="auth-check-text">{t("auth.rememberMe")}</span>
              </label>
              <button type="submit" className="auth-submit" disabled={loading}>
                {loading ? <><span className="auth-spinner"></span> {t("auth.authenticating")}</> : <>{t("auth.accessDashboard")} <i className="fa-solid fa-arrow-right-long"></i></>}
              </button>
              <div className="auth-or"><span>{t("auth.orContinue")}</span></div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <GoogleLogin onSuccess={handleGoogleSuccess} onError={handleGoogleError} useOneTap shape="rectangular" theme="filled_black" size="large" text="continue_with" width="100%" />
              </div>
            </form>
            <p className="auth-switch">{t("auth.noAccount")} <Link to="/register" className="auth-switch-link">{t("auth.createOne")}</Link></p>
            <div className="auth-card-footer"><i className="fa-solid fa-shield-halved"></i> Protected by JWT · Solviotech Admin © {new Date().getFullYear()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;